import React from 'react';
import PropTypes from 'prop-types';
import { Button } from './BaseComponents';

/**
 * ConfirmDialog Component
 * 
 * Modal dialog for delete/reset confirmations.
 * 
 * Usage:
 * <ConfirmDialog
 *   isOpen={showDeleteConfirm}
 *   onClose={() => setShowDeleteConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Quiz löschen?"
 *   message="Diese Aktion kann nicht rückgängig gemacht werden."
 *   confirmLabel="Löschen"
 *   variant="danger"
 * />
 */
export const ConfirmDialog = ({
  isOpen = false,
  onClose,
  onConfirm,
  title = 'Bestätigen',
  message = 'Bist du sicher?',
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  variant = 'danger', // 'danger', 'warning', 'primary'
  loading = false,
  icon = null,
  children = null
}) => {
  if (!isOpen) return null;

  const defaultIcons = {
    danger: '!',
    warning: '!',
    primary: '?'
  };

  const displayIcon = icon || defaultIcons[variant];

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, loading, onClose]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
      >
        {/* Icon */}
        <div className="text-center mb-4">
          <span className="text-3xl font-bold">{displayIcon}</span>
        </div>
        
        {/* Title */}
        <h2 
          id="dialog-title"
          className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2"
        >
          {title}
        </h2>
        
        {/* Message */}
        <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
          {message}
        </p>
        
        {/* Custom content */}
        {children && (
          <div className="mb-6">
            {children}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            fullWidth
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant}
            fullWidth
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  variant: PropTypes.oneOf(['danger', 'warning', 'primary']),
  loading: PropTypes.bool,
  icon: PropTypes.string,
  children: PropTypes.node
};

/**
 * DeleteConfirmDialog - Preset for delete actions
 */
export const DeleteConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  itemName = 'Element',
  loading = false
}) => (
  <ConfirmDialog
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title={`${itemName} löschen?`}
    message={`Möchtest du "${itemName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
    confirmLabel="Löschen"
    variant="danger"
    icon="🗑️"
    loading={loading}
  />
);

DeleteConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemName: PropTypes.string,
  loading: PropTypes.bool
};

/**
 * ResetConfirmDialog - Preset for reset actions
 */
export const ResetConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  itemName = 'Quiz',
  loading = false
}) => (
  <ConfirmDialog
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title={`${itemName} zurücksetzen?`}
    message={`Alle Antworten, Teilnehmer und Punkte werden gelöscht. Das Quiz beginnt von vorne.`}
    confirmLabel="Zurücksetzen"
    variant="warning"
    icon="🔄"
    loading={loading}
  />
);

ResetConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  itemName: PropTypes.string,
  loading: PropTypes.bool
};

export default ConfirmDialog;
